import { useState, useRef, useEffect, useCallback } from 'react'
import { Eye, EyeOff, Send, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import * as telegramService from '@/services/telegramService'
import * as roomService from '@/services/roomService'
import type { NotificationLogEntry } from '@/services/telegramService'

const MOCK_DATA: Record<string, string> = {
  '{{guestName}}': 'Nguyễn Văn A',
  '{{guestPhone}}': '0901 234 567',
  '{{roomName}}': 'G01',
  '{{startTime}}': '14:00',
  '{{endTime}}': '18:00',
  '{{date}}': '20/03/2026',
  '{{totalPrice}}': '676,000đ',
  '{{promoCode}}': '—',
  '{{status}}': 'Confirmed',
}

const TEMPLATE_VARIABLES = [
  '{{guestName}}',
  '{{guestPhone}}',
  '{{roomName}}',
  '{{startTime}}',
  '{{endTime}}',
  '{{date}}',
  '{{totalPrice}}',
  '{{promoCode}}',
  '{{status}}',
]

const EVENT_LABELS: Record<string, string> = {
  new_booking: 'Booking mới',
  confirmed: 'Xác nhận',
  checked_in: 'Check-in',
  checked_out: 'Check-out',
  cancelled: 'Hủy',
  test: 'Kiểm tra',
}

/**
 * Cấu hình Telegram bot — lưu token/chat ID vào DB, soạn template tin nhắn theo sự kiện, xem nhật ký thông báo thật từ server
 */
export function TelegramConfig() {
  const [botToken, setBotToken] = useState('')
  const [chatId, setChatId] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)
  const [loadingConfig, setLoadingConfig] = useState(true)

  const [templates, setTemplates] = useState<telegramService.NotificationTemplate[]>([])
  const [selectedEventType, setSelectedEventType] = useState<string>('new_booking')
  const [templateText, setTemplateText] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const [log, setLog] = useState<NotificationLogEntry[]>([])
  const [rooms, setRooms] = useState<any[]>([])

  const fetchConfig = useCallback(async () => {
    setLoadingConfig(true)
    try {
      const config = await telegramService.getConfig()
      setBotToken(config.botToken || '')
      setChatId(config.chatId || '')
      setEnabled(config.enabled ?? false)
      setConfigSaved(!!(config.botToken && config.chatId))
    } catch {
      toast.error('Không thể tải cấu hình Telegram')
    } finally {
      setLoadingConfig(false)
    }
  }, [])

  const fetchTemplates = useCallback(async () => {
    try {
      const tpls = await telegramService.getTemplates()
      setTemplates(tpls)
      const currentTpl = tpls.find((t) => t.eventType === selectedEventType)
      setTemplateText(currentTpl ? currentTpl.content : '')
    } catch {
      toast.error('Không thể tải danh sách mẫu tin nhắn')
    }
  }, [selectedEventType])

  const fetchLog = useCallback(async () => {
    try {
      const res = await telegramService.getLog(1, 50)
      setLog(res.data)
    } catch {
      toast.error('Không thể tải nhật ký thông báo')
    }
  }, [])

  const fetchRooms = useCallback(async () => {
    try {
      const data = await roomService.getAll()
      setRooms(data)
    } catch {}
  }, [])

  useEffect(() => {
    fetchConfig()
    fetchLog()
    fetchRooms()
  }, [fetchConfig, fetchLog, fetchRooms])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const handleSaveConfig = async () => {
    try {
      await telegramService.saveConfig({ botToken, chatId, enabled })
      setConfigSaved(true)
      toast.success('Đã lưu cấu hình!')
      fetchConfig()
    } catch (err: any) {
      toast.error(err.message || 'Lưu cấu hình thất bại')
    }
  }

  const handleSendTest = async () => {
    try {
      const res = await telegramService.sendTest()
      toast.success(res.message || 'Đã gửi tin kiểm tra!')
      fetchLog()
    } catch (err: any) {
      toast.error(err.message || 'Gửi tin kiểm tra thất bại')
    }
  }

  const handleSaveTemplate = async () => {
    try {
      await telegramService.saveTemplate(selectedEventType, templateText)
      toast.success('Đã lưu mẫu tin!')
      fetchTemplates()
    } catch (err: any) {
      toast.error(err.message || 'Lưu mẫu tin thất bại')
    }
  }

  const handleInsertVariable = (variable: string) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newValue =
      templateText.slice(0, start) + variable + templateText.slice(end)
    setTemplateText(newValue)
    requestAnimationFrame(() => {
      textarea.focus()
      const newPos = start + variable.length
      textarea.setSelectionRange(newPos, newPos)
    })
  }

  const previewText = TEMPLATE_VARIABLES.reduce(
    (text, variable) => text.replaceAll(variable, MOCK_DATA[variable]),
    templateText,
  )

  const botDisplayName = configSaved && botToken ? '@NhaCamBot' : null

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-bold text-foreground">Cấu hình Telegram</h2>

      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-foreground">
          Cấu hình Bot
        </h3>

        <div className="mb-5 flex items-center gap-2">
          <span
            className={cn(
              'inline-block h-2.5 w-2.5 rounded-full',
              enabled && configSaved ? 'bg-status-success' : 'bg-muted-foreground',
            )}
          />
          <span className="text-sm text-muted-foreground">
            {enabled && configSaved
              ? `Đã kết nối và kích hoạt — ${botDisplayName}`
              : configSaved
              ? `Đã cấu hình nhưng đang tắt gửi thông báo — ${botDisplayName}`
              : 'Chưa cấu hình'}
          </span>
        </div>

        <div className="mb-4 flex flex-col gap-1.5">
          <Label htmlFor="bot-token">Bot Token</Label>
          <div className="relative">
            <Input
              id="bot-token"
              type={showToken ? 'text' : 'password'}
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showToken ? 'Ẩn token' : 'Hiện token'}
            >
              {showToken ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-1.5">
          <Label htmlFor="chat-id">Chat ID</Label>
          <Input
            id="chat-id"
            type="text"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder="-1001234567890"
            className="font-mono"
          />
        </div>

        <div className="mb-5 flex items-center gap-2">
          <input
            type="checkbox"
            id="telegram-enabled"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor="telegram-enabled" className="cursor-pointer font-normal text-sm">
            Kích hoạt gửi thông báo tự động cho khách hàng
          </Label>
        </div>

        <div className="flex gap-3">
          <Button
            variant="primary"
            onClick={handleSaveConfig}
            disabled={!botToken.trim() || !chatId.trim()}
          >
            Lưu cấu hình
          </Button>
          <Button variant="outline" onClick={handleSendTest} disabled={!configSaved}>
            <Send className="h-4 w-4" />
            Gửi tin kiểm tra
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-foreground">
          Mẫu tin nhắn
        </h3>

        <div className="mb-4 flex flex-col gap-1.5 max-w-xs">
          <Label htmlFor="event-type-select">Sự kiện kích hoạt</Label>
          <select
            id="event-type-select"
            value={selectedEventType}
            onChange={(e) => setSelectedEventType(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            <option value="new_booking">Booking mới (new_booking)</option>
            <option value="confirmed">Xác nhận đặt phòng (confirmed)</option>
            <option value="checked_in">Check-in nhận phòng (checked_in)</option>
          </select>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Label htmlFor="template-editor">Nội dung mẫu</Label>
            <textarea
              ref={textareaRef}
              id="template-editor"
              value={templateText}
              onChange={(e) => setTemplateText(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-border bg-muted px-3 py-2 font-mono text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-primary/30"
            />

            <div className="flex flex-wrap gap-1.5">
              {TEMPLATE_VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleInsertVariable(v)}
                  className="rounded-md bg-status-info-muted px-2 py-0.5 font-mono text-xs text-status-info-foreground transition-colors hover:bg-status-info-muted/80"
                >
                  {v}
                </button>
              ))}
            </div>

            <Button variant="primary" onClick={handleSaveTemplate}>
              Lưu mẫu
            </Button>
          </div>

          <div className="flex flex-col">
            <div className="rounded-xl bg-[#0E1621] p-4">
              <p className="mb-3 text-xs font-medium text-slate-400">
                Xem trước — Telegram
              </p>
              <div className="max-w-sm rounded-xl bg-[#182533] px-4 py-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
                  {previewText || 'Chưa cấu hình mẫu tin nhắn.'}
                </p>
                <div className="mt-2 flex items-center justify-end gap-2">
                  <span className="text-[10px] text-slate-500">
                    NhaCam Bot
                  </span>
                  <span className="text-[10px] text-slate-500">14:00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h3 className="mb-4 text-base font-semibold text-foreground">
          Sự kiện & Nhật ký thông báo
        </h3>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-status-success/20 bg-status-success-muted px-4 py-3">
            <p className="text-sm font-medium text-status-success-foreground">
              Tự động gửi khi:
            </p>
            <p className="mt-1 text-sm text-status-success-foreground">
              Booking mới &middot; Xác nhận &middot; Check-in
            </p>
          </div>

          <div className="rounded-lg border border-status-error/20 bg-status-error-muted px-4 py-3">
            <p className="text-sm font-medium text-status-error-foreground">
              Không gửi khi:
            </p>
            <p className="mt-1 text-sm text-status-error-foreground">
              Nội bộ (dọn/bảo trì) &middot; Hủy &middot; Check-out
            </p>
          </div>
        </div>

        {log.length === 0 ? (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-12">
            <p className="text-sm text-muted-foreground">Chưa có thông báo nào.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-2 pr-4 font-medium text-muted-foreground w-[160px]">
                    Thời gian
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground w-[110px]">
                    Sự kiện
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground">
                    Khách &middot; Phòng
                  </th>
                  <th className="pb-2 font-medium text-muted-foreground w-[120px]">
                    Trạng thái
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-border last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-muted-foreground">
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td className="py-2.5 pr-4 text-foreground">
                      {EVENT_LABELS[entry.event] || entry.event}
                    </td>
                    <td className="py-2.5 pr-4 text-foreground">
                      {entry.guestName} &middot; {rooms.find((r) => r.id === entry.roomName)?.name || entry.roomName}
                    </td>
                    <td className="py-2.5">
                      <LogStatusBadge status={entry.status} errorMessage={entry.errorMessage} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function LogStatusBadge({
  status,
  errorMessage,
}: {
  status: NotificationLogEntry['status']
  errorMessage?: string | null
}) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-success-muted px-2.5 py-0.5 text-xs font-medium text-status-success-foreground">
        Đã gửi ✓
      </span>
    )
  }
  if (status === 'simulated') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-warning-muted px-2.5 py-0.5 text-xs font-medium text-status-warning-foreground">
        Mô phỏng
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-status-error-muted px-2.5 py-0.5 text-xs font-medium text-status-error-foreground cursor-help"
        title={errorMessage || 'Lỗi không xác định'}
      >
        Thất bại ✗
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      Bỏ qua
    </span>
  )
}
